import * as React from 'react';
import { MoreHorizontal } from 'lucide-react';

import { cn } from '@/helpers/ui-utils';
import { Slot } from '@radix-ui/react-slot';

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn('mx-auto flex w-full justify-center', className)}
    {...props}
  />
);

Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn('flex flex-row items-center gap-2', className)}
    {...props}
  />
));

PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'>
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('group/pagination-item', className)} {...props} />
));

PaginationItem.displayName = 'PaginationItem';

export type PaginationLinkProps = {
  /**
   * Whether this pagination link represents the currently active page.
   * @default false
   */
  isActive?: boolean;

  /**
   * Render as child component using Radix Slot. Allows composition with other elements while maintaining pagination styling.
   * @default false
   */
  asChild?: boolean;

  /**
   * Whether the pagination link is disabled. Prevents interaction and applies disabled styling.
   * @default false
   */
  disabled?: boolean;
} & React.ComponentProps<'a'>;

const PaginationLink = ({
  className,
  isActive,
  asChild = false,
  ...props
}: PaginationLinkProps) => {
  const Comp = asChild ? Slot : 'a';

  return (
    <Comp
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        [
          'text-foreground',
          'bg-transparent',
          'py-2',
          'px-[14px]',
          'transition-colors',
          'duration-300',
          'ease-in',
          'rounded-xs',
          'hover:bg-[#FAFAFA]',
          'hover:no-underline',
          'aria-[current=page]:bg-[#FFF4F0]',
          'aria-[current=page]:text-primary',
        ],
        {
          'pointer-events-none opacity-50': props.disabled,
        },
        className,
      )}
      {...props}
    />
  );
};
PaginationLink.displayName = 'PaginationLink';

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<'span'>) => (
  <span
    aria-hidden
    className={cn('flex h-9 w-9 items-center justify-center', className)}
    {...props}
  >
    <MoreHorizontal className="h-6 w-6" />
    <span className="sr-only">More pages</span>
  </span>
);

PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
};
